import { useTranslation } from "react-i18next";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppForms from "@toothfairy/shared-ui/AppForms";
import { copyToClipboard } from "@toothfairy/shared-ui/ClipboardUtils";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useCallback, useEffect, useState } from "react";
import { View } from "react-native";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import telegramAPI from "../../../../API/Channels/telegramAPI";
import Constants from "../../../../Constants";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { isLargeScreen } from "../../../MainViewContainer";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AdminInfoLeaf from "../../../Admin/Components/SubComponents/AdminInfoLeaf";
import IdCopyView from "../../../../Components/ToothFairyComponents/IdCopyView";
import envConfig from "../../../../../envConfig";
import {
  BuilderSection,
  PassthroughSection,
  useBuilderDirtyTracking,
} from "./BuilderSections";

const provider_types = [
  {
    id: "twilio",
    name: "Twilio",
    type: "sms",
    is_enterprise: false,
  },
  {
    id: "whatsapp",
    name: "Meta",
    type: "whatsapp",
    is_enterprise: false,
  },
  {
    id: "twilio",
    name: "Twilio for Whatsapp",
    type: "whatsapp",
    is_enterprise: false,
  },
  {
    id: "ses",
    name: "Custom Domain Email",
    type: "email",
    is_enterprise: true,
  },
  {
    id: "twilio",
    name: "SIP integration with Twilio",
    type: "sip",
    is_enterprise: true,
  },
  {
    id: "telegram",
    name: "Telegram",
    type: "telegram",
    is_enterprise: false,
  },
];

const channel_types = [
  {
    id: "sms",
    name: "SMS",
    is_enterprise: false,
  },
  {
    id: "whatsapp",
    name: "Whatsapp",
    is_enterprise: false,
  },
  {
    id: "email",
    name: "Email",
    is_enterprise: false,
  },
  {
    id: "sip",
    name: "SIP Phone",
    is_enterprise: true,
  },
  {
    id: "telegram",
    name: "Telegram",
    is_enterprise: false,
  },
];

const CHANNEL_SECTION_FIELD_MAP = {
  profile: ["name", "description", "channel", "provider"],
  credentials: ["senderid", "tokenSecret"],
  setup: [],
};

const ChannelSettingsAIView = ({
  isEdit,
  settingType,
  handleDeletion,
  isSaveInProgress,
  onUpdateSecret,
  isUpdatingSecret,
  isBuilderLayout,
  builderActiveSection,
  onBuilderContextChange,
  saveVersion,
}) => {
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const { currentTheme, mode } = AppTheme.useTheme();
  const { getPlanConfig, getActiveWorkspace } = useWorkspaces();
  const entityName = "Channel mapping";
  const sipDomain = envConfig?.REACT_APP_SIP_DOMAIN || "sip.toothfairyai.com";
  const {
    setFieldValue,
    values,
    validationSchema,
    initialValues,
    errors,
    submitCount,
  } = AppForms.useFormikContext();
  const [initialLoadCompleted, setInitialLoadCompleted] = React.useState(false);
  const [sipConfig, setSipConfig] = useState(null);

  const _showBuilderSection = (sectionId) =>
    builderActiveSection == null || builderActiveSection === sectionId;
  const SectionRenderer = isBuilderLayout ? BuilderSection : PassthroughSection;

  useBuilderDirtyTracking({
    isBuilderLayout,
    values,
    initialValues,
    saveVersion,
    onBuilderContextChange,
    errors,
    submitCount,
    sectionFieldMap: CHANNEL_SECTION_FIELD_MAP,
  });

  // SIP config is constructed from workspace ID and env config
  // The origination URI format is: sip:<workspace_id>@<sip_domain>:5060
  useEffect(() => {
    if (values?.channel === "sip" && values?.id && getActiveWorkspace()?.id) {
      // Construct SIP config from workspace ID and env
      const workspaceId = getActiveWorkspace().id;
      const sipDomain =
        envConfig?.REACT_APP_SIP_DOMAIN || "sip.toothfairyai.com";
      setSipConfig({
        inbound: {
          origination_uri: `sip:${workspaceId}@${sipDomain}:5060`,
        },
      });
    }
  }, [values?.channel, values?.id]);
  const _title = useCallback(() => {
    return isEdit ? t("settings.channels.save") : t("settings.channels.create");
  }, [entityName, isEdit]);

  const handleUpdateSecretClick = useCallback(() => {
    setFieldValue("tokenSecret", "");
    onUpdateSecret();
  }, [setFieldValue, onUpdateSecret]);

  const [isTelegramWebhookLoading, setIsTelegramWebhookLoading] = useState(
    false,
  );
  const handleRegisterTelegramWebhook = useCallback(async () => {
    const workspaceId = getActiveWorkspace()?.id;
    if (!values?.id || !workspaceId) return;
    setIsTelegramWebhookLoading(true);
    try {
      const result = await telegramAPI.registerTelegramWebhook({
        workspace_id: workspaceId,
        channel_id: values?.id,
      });
      const isSuccess =
        result?.data?.success === true || result?.success === true;
      if (!isSuccess) {
        throw new Error(
          result?.data?.error || result?.error || "Registration failed",
        );
      }
      AppToasty.show(t("settings.channels.telegramWebhookRegistered"), {
        type: "success",
      });
    } catch (error) {
      AppToasty.show(
        t("settings.channels.telegramWebhookFailed", {
          error: error?.message || "",
        }),
        { type: "danger" },
      );
    } finally {
      setIsTelegramWebhookLoading(false);
    }
  }, [values?.id, getActiveWorkspace, AppToasty, t]);

  useEffect(() => {
    setTimeout(() => {
      setInitialLoadCompleted(true);
    }, [2000]);
  }, []);

  useEffect(() => {
    if (
      values?.channel &&
      (!values?.provider ||
        !provider_types
          ?.filter((u) => u?.type == values?.channel)
          ?.includes(values?.provider)) &&
      values?.id == null
    ) {
      if (values?.channel == "sms") setFieldValue("provider", "twilio");
      else if (values?.channel == "whatsapp")
        setFieldValue("provider", "whatsapp");
      else if (values?.channel == "email") setFieldValue("provider", "ses");
      else if (values?.channel == "sip") setFieldValue("provider", "twilio");
      else if (values?.channel == "telegram")
        setFieldValue("provider", "telegram");
    } else if (!values?.channel && !values?.provider) {
      setFieldValue("channel", "sms");
      setFieldValue("provider", "twilio");
    }
  }, [values?.channel]);
  return (
    <>
      {_showBuilderSection("profile") && (
        <SectionRenderer
          title={t("channels.builderSectionProfile", {
            defaultValue: "Overview",
          })}
          icon={AppAssets.icons.persona}
        >
          {isEdit && (
            <IdCopyView id={values?.id} label={t("settings.channels.id")} />
          )}
          <AppForms.AppFormField
            showTextInputLabel
            isMandatory
            wrapperInputStyle={{
              paddingTop: 10,
            }}
            label={t("settings.channels.providerName")}
            placeholder={t("settings.channels.providerName")}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="name"
          />
          <AppForms.AppFormField
            showTextInputLabel
            wrapperInputStyle={{
              paddingTop: 10,
            }}
            multiline
            textContainer={{
              minHeight: 120,
              paddingVertical: 10,
              marginHorizontal: 5,
            }}
            label={t("settings.channels.description")}
            placeholder={t("settings.channels.descriptionPlaceholder")}
            keyboardtype="default"
            textContextType="label"
            autoCorrect={false}
            name="description"
          />
          <AppForms.AppFormDropDown
            isElevated={false}
            dropDownStyle={{
              borderColor: currentTheme?.theme?.border_color,
              maxWidth: !isLargeScreen() && 250,
            }}
            showTextInputLabel
            label={t("settings.channels.channelType")}
            isMandatory
            name="channel"
            items={channel_types?.filter(
              (u) =>
                u?.is_enterprise != true ||
                (u?.is_enterprise &&
                  getActiveWorkspace()?.subscriptionType == "enterprise"),
            )}
            propertyLabel="name"
            propertyValue="id"
          />
          <AppForms.AppFormDropDown
            isElevated={false}
            dropDownStyle={{
              borderColor: currentTheme?.theme?.border_color,
              maxWidth: !isLargeScreen() && 250,
            }}
            showTextInputLabel
            label={t("settings.channels.provider")}
            isMandatory
            name="provider"
            items={provider_types?.filter((u) => u?.type === values?.channel)}
            propertyLabel="name"
            propertyValue="id"
          />
        </SectionRenderer>
      )}
      {_showBuilderSection("credentials") && (
        <SectionRenderer
          title={t("channels.builderSectionCredentials", {
            defaultValue: "Credentials",
          })}
          icon={AppAssets.icons.credentials}
        >
          {(values?.channel == "sms" ||
            values?.channel == "whatsapp" ||
            values?.channel == "sip") && (
            <View
              style={{
                zIndex: -1,
              }}
            >
              <AppForms.AppFormField
                showTextInputLabel
                wrapperInputStyle={{
                  paddingTop: 10,
                }}
                isMandatory
                label={
                  values?.channel == "sip"
                    ? t("settings.channels.twilioAccountSidMandatory")
                    : t("settings.channels.accountId")
                }
                placeholder={
                  values?.channel == "sip"
                    ? t("settings.channels.twilioAccountSidPlaceholder")
                    : t("settings.channels.accountIdPlaceholder")
                }
                keyboardtype="default"
                textContextType="label"
                autoCorrect={false}
                name="senderid"
              />
            </View>
          )}
          {!["ses"].includes(values?.provider) && (
            <View style={{ position: "relative" }}>
              <AppForms.AppFormField
                showTextInputLabel
                isMandatory
                wrapperInputStyle={{
                  paddingTop: 10,
                }}
                disabled={isEdit && !isUpdatingSecret}
                label={
                  values?.channel == "sip"
                    ? t("settings.channels.twilioAuthTokenMandatory")
                    : t("settings.channels.token")
                }
                placeholder={
                  isEdit && !isUpdatingSecret
                    ? values?.channel == "sip"
                      ? t("settings.channels.authTokenHidden")
                      : t("settings.channels.apiKeyHidden")
                    : isEdit && isUpdatingSecret
                    ? values?.channel == "sip"
                      ? t("settings.channels.enterNewAuthToken")
                      : t("settings.channels.enterNewApiKey")
                    : values?.channel == "sip"
                    ? t("settings.channels.twilioAuthTokenPlaceholder")
                    : t("settings.channels.apiKeyPlaceholder")
                }
                keyboardtype="default"
                textContextType="password"
                autoCorrect={false}
                secureTextEntry={true}
                name={isEdit && !isUpdatingSecret ? "$xc" : "tokenSecret"}
              />
              {isEdit && !isUpdatingSecret && (
                <ToothFairyButton
                  title={
                    values?.channel == "sip"
                      ? t("settings.channels.updateToken")
                      : t("settings.channels.updateApiKey")
                  }
                  onPress={handleUpdateSecretClick}
                  style={{
                    height: 25,
                    paddingHorizontal: 0,
                  }}
                  btnContentStyle={{
                    paddingHorizontal: 5,
                  }}
                  backgroundColor={currentTheme?.theme?.soft_primary}
                  wrapperStyle={{
                    position: "absolute",
                    right: 0,
                    top: 49,
                    minWidth: 120,
                    height: 25,
                    zIndex: 10,
                  }}
                />
              )}
            </View>
          )}
        </SectionRenderer>
      )}
      {_showBuilderSection("setup") && isEdit && (
        <SectionRenderer
          title={t("channels.builderSectionSetup", {
            defaultValue: "Setup",
          })}
          icon={AppAssets.icons.webhook}
        >
          {values?.channel === "sip" && (
            <View style={{ width: "100%", paddingLeft: 10, paddingRight: 30 }}>
              {values?.id && sipConfig ? (
                <>
                  <AdminInfoLeaf
                    wrapperStyle={{
                      alignSelf: "flex-start",
                      alignItems: "flex-start",
                      width: "100%",
                      marginTop: 10,
                    }}
                    marginLeft={0}
                    value={
                      sipConfig?.inbound?.origination_uri ||
                      `sip:${getActiveWorkspace()?.id}@${sipDomain}:5060`
                    }
                    showSimpleText
                    simpleTextWrapperStyle={{
                      paddingVertical: 10,
                    }}
                    handleClick={async () => {
                      const uri =
                        sipConfig?.inbound?.origination_uri ||
                        `sip:${getActiveWorkspace()?.id}@${sipDomain}:5060`;
                      const success = await copyToClipboard(uri);
                      if (success) {
                        AppToasty.show(
                          t("settings.channels.originationUriCopied"),
                          { type: "success" },
                        );
                      }
                    }}
                    caption={t("settings.channels.sipOriginationCaption")}
                    title={t("settings.channels.sipOriginationTitle")}
                    accessibilityLabel="SIP Origination URI"
                    hideShowIcon={true}
                  />

                  <AdminInfoLeaf
                    wrapperStyle={{
                      alignSelf: "flex-start",
                      alignItems: "flex-start",
                      width: "100%",
                      marginTop: 0,
                    }}
                    marginLeft={0}
                    value={t("settings.channels.twilioSetupInstructions")}
                    showSimpleText
                    showCopyIconOnSimpleText={false}
                    simpleTextWrapperStyle={{
                      paddingVertical: 10,
                    }}
                    title={t("settings.channels.twilioSetupTitle")}
                    accessibilityLabel={t("settings.channels.twilioSetupTitle")}
                    hideShowIcon={true}
                  />
                </>
              ) : (
                <AdminInfoLeaf
                  wrapperStyle={{
                    alignSelf: "flex-start",
                    alignItems: "flex-start",
                    width: 350,
                    marginTop: -5,
                  }}
                  marginLeft={0}
                  value={t("settings.channels.sipConfigWarning")}
                  showSimpleText
                  simpleTextWrapperStyle={{
                    paddingVertical: 10,
                  }}
                  title={t("settings.channels.sipConfiguration")}
                  accessibilityLabel={t(
                    "settings.channels.sipConfigurationWarning",
                  )}
                  hideShowIcon={true}
                />
              )}
            </View>
          )}
          {values?.id && values?.provider && values?.channel != "sip" && (
            <AdminInfoLeaf
              wrapperStyle={{
                alignSelf: "flex-start",
                alignItems: "flex-start",
                width: "100%",
                paddingLeft: 10,
                paddingRight: 20,
              }}
              marginLeft={0}
              value={`https://api.toothfairyai.com/webhooks/${
                values?.provider
              }/${getActiveWorkspace()?.id}`}
              showSimpleText
              simpleTextWrapperStyle={{
                paddingVertical: 10,
              }}
              handleClick={async () => {
                const url = `https://api.toothfairyai.com/webhooks/${
                  values?.provider
                }/${getActiveWorkspace()?.id}`;
                const success = await copyToClipboard(url);
                if (success) {
                  AppToasty.show(t("settings.channels.webhookUrlCopied"), {
                    type: "success",
                  });
                } else {
                  AppToasty.show(t("settings.channels.couldNotCopyValue"), {
                    type: "danger",
                  });
                }
              }}
              caption={t("settings.channels.webhookCaption")}
              subcaption={t("settings.channels.webhookSubcaption")}
              title={t("settings.channels.channelWebhook")}
              accessibilityLabel={t("settings.channels.channelWebhook")}
              hideShowIcon={true}
            />
          )}
          {values?.channel === "telegram" && values?.id && (
            <View
              style={{
                width: "100%",
                paddingLeft: 10,
                paddingRight: 20,
                marginTop: 10,
                marginLeft: -10,
              }}
            >
              <ToothFairyButton
                title={t("settings.channels.telegramRegisterWebhook")}
                onPress={handleRegisterTelegramWebhook}
                isDisabled={isTelegramWebhookLoading}
                wrapperStyle={{
                  marginTop: 10,
                  minWidth: 250,
                  height: Constants.appButtonHeight,
                }}
              />
            </View>
          )}
        </SectionRenderer>
      )}
      {!isBuilderLayout && (
        <View style={{ alignItems: "center" }}>
          <AppForms.AppSubmitButton
            title={_title()}
            awesomeIconColor={currentTheme?.theme?.white}
            backgroundColor={currentTheme?.theme?.primary}
            isDisabled={isSaveInProgress}
            btnContentStyle={{
              paddingLeft: 0,
              paddingRight: 0,
            }}
            style={[
              {
                height: Constants.appButtonHeight,
                width: "auto",
                shadowColor: currentTheme?.theme?.transparent,
                marginHorizontal: 10,
              },
            ]}
            // btnLabelStyle={{ fontSize: 16 }}
            wrapperStyle={[
              {
                width: "fit-content",
                minWidth: 150,
                marginTop: 20,
                height: Constants.appButtonHeight,
              },
            ]}
          />
          {isEdit && (
            <ToothFairyButton
              title={t("settings.channels.delete")}
              onPress={handleDeletion}
              isDeletion
              wrapperStyle={{
                marginTop: 30,
                minWidth: 150,
                height: Constants.appButtonHeight,
              }}
            />
          )}
        </View>
      )}
    </>
  );
};

export default ChannelSettingsAIView;
